import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheck, FiX } from 'react-icons/fi';
import { useI18n } from '../i18n/useI18n';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

/**
 * Telegram 图标组件
 */
function TelegramIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-6 h-6"
    >
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.248-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.14.121.098.154.23.17.324.015.094.035.308.02.476z" />
    </svg>
  );
}

export function InvitePage() {
  const { code } = useParams();
  const { t, lang } = useI18n();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const botUsername = import.meta.env.VITE_BOT_USERNAME || 'PolyOneAI_bot';

  useEffect(() => {
    if (code) {
      // 验证邀请码
      const url = new URL(`${API_BASE_URL}/api/v1/invite/info/${code}`);
      url.searchParams.set('lang', lang);
      fetch(url.toString())
        .then(res => res.json())
        .then(data => {
          if (data.code === 0 && data.data?.valid) {
            setInviteInfo(data.data);
            // 保存邀请码到 localStorage
            localStorage.setItem('invite_code', code.toUpperCase());
          } else {
            setError(data.data?.message || t('invite.invalidTitle'));
          }
        })
        .catch(err => {
          console.error('验证邀请码失败:', err);
          setError(t('invite.verifyFailed'));
        })
        .finally(() => setLoading(false));
    }
  }, [code, lang, t]);

  const handleTelegramJoin = () => {
    // 跳转到 Telegram Bot（使用邀请码）
    window.location.href = `https://t.me/${botUsername}?start=${code?.toUpperCase()}`;
  };

  // 加载状态
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-[420px] p-8 flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-slate-700/30 border-t-accent rounded-full animate-spin"></div>
          <p className="mt-4 text-slate-400 text-sm">{t('invite.verifying')}</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-[420px] p-8 flex flex-col items-center justify-center text-center">
          <FiX className="w-16 h-16 text-red-500 mb-4" />
          <h1 className="text-2xl font-semibold text-white mb-3">{t('invite.invalidTitle')}</h1>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2.5 rounded-xl bg-slate-700/40 hover:bg-slate-700/60 border border-slate-600/50 hover:border-slate-500 text-white text-sm font-medium transition-all duration-200"
          >
            {t('common.backHome')}
          </button>
        </div>
      </div>
    );
  }

  // 成功状态 - 精简版邀请页面
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="glass-xl w-full max-w-[420px] p-8">
        {/* 头部 */}
        <div className="text-center mb-8">
          <FiCheck className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {t('invite.welcome', { brand: 'PolyOne' })}
          </h1>
          <p className="text-sm text-slate-400 mb-6">
            {t('invite.inviteCode')}: <span className="text-accent text-lg font-semibold tracking-wider font-mono">{inviteInfo?.invite_code}</span>
          </p>
          <p className="text-slate-300/80 text-sm leading-relaxed">
            {t('invite.tagline').split('\n')[0]}<br />
            {t('invite.tagline').split('\n')[1]}
          </p>
        </div>

        {/* Telegram 注册按钮 */}
        <button
          onClick={handleTelegramJoin}
          className="flex items-center justify-center gap-3 w-full px-6 py-4 rounded-2xl bg-[#0088cc] hover:bg-[#0088cc]/90 text-white font-semibold text-base transition-all duration-200 cursor-pointer border-none shadow-lg hover:shadow-xl"
        >
          <TelegramIcon />
          <span>{t('invite.joinViaTelegram')}</span>
        </button>

        {/* 底部说明 */}
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <p className="text-xs text-slate-500 text-center leading-relaxed">
            {t('invite.firstLoginHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
